import React, { useEffect, useState } from "react";
import { Paintbrush } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { errMsg } from "@/lib/api";
import {
  usePlatformBranding,
  useUpdatePlatformBranding,
  useClearPlatformConfigurationCache,
} from "@/lib/platformConfig";

const DEFAULT_BRANDING = {
  app_name: "EduOS",
  client_name: "EduOS",
};

export default function PlatformBranding() {
  const brandingQuery = usePlatformBranding();
  const updateBranding = useUpdatePlatformBranding();
  const clearCache = useClearPlatformConfigurationCache();

  const [appName, setAppName] = useState(DEFAULT_BRANDING.app_name);
  const [clientName, setClientName] = useState(DEFAULT_BRANDING.client_name);

  useEffect(() => {
    if (!brandingQuery.data) return;
    setAppName(brandingQuery.data.app_name || DEFAULT_BRANDING.app_name);
    setClientName(brandingQuery.data.client_name || DEFAULT_BRANDING.client_name);
  }, [brandingQuery.data]);

  const save = (e) => {
    e.preventDefault();
    updateBranding.mutate(
      { app_name: appName, client_name: clientName },
      {
        onSuccess: () => {
          toast.success("Branding updated");
        },
        onError: (error) => {
          toast.error(errMsg(error));
        },
      }
    );
  };

  const resetToDefaults = () => {
    setAppName(DEFAULT_BRANDING.app_name);
    setClientName(DEFAULT_BRANDING.client_name);
  };

  const refreshConfigCache = () => {
    clearCache.mutate(
      { key: "branding" },
      {
        onSuccess: () => toast.success("Branding cache refreshed"),
        onError: (error) => toast.error(errMsg(error)),
      }
    );
  };

  if (brandingQuery.isLoading) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl" data-testid="platform-branding-page">
        <Skeleton className="h-9 w-64 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-3xl" data-testid="platform-branding-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)] flex items-center gap-2">
          <Paintbrush className="h-6 w-6 text-[var(--accent)]" /> Branding
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          Configure deployment-level brand identity shown in login, shell, and browser title.
        </p>
      </div>

      <Card className="border-[var(--subtle)]">
        <CardHeader>
          <CardTitle className="font-display">Brand Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={save}>
            <div>
              <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Application Name</Label>
              <Input
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="EduOS"
                className="mt-1"
                data-testid="branding-app-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-[var(--text-secondary)]">Client / School Name</Label>
              <Input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Greenwood International"
                className="mt-1"
                data-testid="branding-client-name"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="submit"
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                disabled={updateBranding.isPending}
                data-testid="branding-save"
              >
                {updateBranding.isPending ? "Saving..." : "Save branding"}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={refreshConfigCache}
                disabled={clearCache.isPending}
                data-testid="branding-refresh-cache"
              >
                {clearCache.isPending ? "Refreshing..." : "Refresh cache"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={resetToDefaults}
                data-testid="branding-reset-default"
              >
                Reset to defaults
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
